// What sent this visitor here, captured once and kept for 30 days so an
// enquiry weeks later still points back at the share that started it. First
// touch wins — the share that did the work, not the last page before the form.
const KEY = "cass_attribution";
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

const SOCIAL_HOSTS = [
  ["linkedin", "linkedin."],
  ["facebook", "facebook."],
  ["facebook", "fb."],
  ["whatsapp", "whatsapp."],
  ["instagram", "instagram."],
  ["x", "t.co"],
  ["x", "twitter."],
  ["x", "x.com"],
];

// Organic search carries no UTM parameters, so a visit from Google used to be
// stored with no channel at all and had to be inferred from the referring host
// every time the report was read. These are the engines worth naming; anything
// else with a referrer is a referral, and anything with none is direct.
const SEARCH_HOSTS = [
  ["google", "google."],
  ["bing", "bing."],
  ["duckduckgo", "duckduckgo."],
  ["yahoo", "search.yahoo."],
  ["ecosia", "ecosia."],
  ["brave", "search.brave."],
  ["startpage", "startpage."],
  ["qwant", "qwant."],
  ["baidu", "baidu."],
  ["yandex", "yandex."],
];

// AI assistants send real visitors now, and they are not organic search. Kept
// apart so "found us through an AI assistant" never gets counted as Google.
const ASSISTANT_HOSTS = [
  ["chatgpt", "chatgpt.com"],
  ["chatgpt", "chat.openai.com"],
  ["perplexity", "perplexity.ai"],
  ["claude", "claude.ai"],
  ["gemini", "gemini.google.com"],
  ["copilot", "copilot.microsoft.com"],
];

const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw || !raw.first_seen) return null;
    if (Date.now() - new Date(raw.first_seen).getTime() > MAX_AGE) {
      localStorage.removeItem(KEY);
      return null;
    }
    return raw;
  } catch {
    return null;
  }
};

/** The channel a referring host represents. Decided once, on arrival, so the
 *  report reads a stored fact instead of re-guessing from the referrer. */
export const channelFor = (host, sameHost) => {
  if (!host || host === sameHost) return { source: "", medium: "" };
  const search = SEARCH_HOSTS.find(([, frag]) => host.includes(frag));
  if (search) {
    // gemini.google.com contains "google." and is an assistant, not a search.
    const assistant = ASSISTANT_HOSTS.find(([, frag]) => host.includes(frag));
    if (assistant) return { source: assistant[0], medium: "ai_assistant" };
    return { source: search[0], medium: "organic" };
  }
  const assistant = ASSISTANT_HOSTS.find(([, frag]) => host.includes(frag));
  if (assistant) return { source: assistant[0], medium: "ai_assistant" };
  const social = SOCIAL_HOSTS.find(([, frag]) => host.includes(frag));
  if (social) return { source: social[0], medium: "social" };
  return { source: host, medium: "referral" };
};

/** Called on arrival. Keeps the first thing that sent them and leaves it alone. */
export const captureAttribution = (path) => {
  if (read()) return;

  const params = new URLSearchParams(window.location.search);
  const utm = {
    source: params.get("utm_source") || "",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
    content: params.get("utm_content") || "",
    term: params.get("utm_term") || "",
  };
  let referrerHost = "";
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    if (host && host !== window.location.hostname) referrerHost = host;
  } catch {
    /* no referrer — most apps strip it, and that stays direct */
  }

  const channel = channelFor(referrerHost, window.location.hostname);
  const record = {
    // UTM always wins: a tagged link is something CASS set up deliberately.
    source: utm.source || channel.source,
    medium: utm.medium || channel.medium,
    campaign: utm.campaign,
    content: utm.content,
    term: utm.term,
    landing: path,
    referrer: referrerHost,
    first_seen: new Date().toISOString(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* private browsing — the enquiry simply arrives unattributed */
  }
};

/** Handed over with every enquiry form. Null when there's nothing to say. */
export const getAttribution = () => read();
